import * as React from 'react'
import { ScrollView, Text, TouchableOpacity, View, Image, Linking, FlatList, Platform } from 'react-native'
import styled from '@theme/styled-components'

import Header from '@components/header/Header'
import Loader from '@components/loader/Loader'
import Icon from '@components/icon/Icons'
import Button from '@components/button/Button'
import Switch from '@components/switch/Switch'
import Drawer from '@components/drawer/Drawer'
import ModalSearch from '@components/modal/ModalSearch'
import IncidentCommentInput from './IncidentCommentInput'
import IncidentStatus, { LARGE_SIZE } from '@components/incidentStatus/IncidentStatus'
import IncidentManifestation from './IncidentManifestation'
import Modal from '@components/modal/Modal'

import * as LocationStore from '@store/location/location'
import * as FamilleStore from '@store/famille/famille'
import * as SocieteStore from '@store/societe/societe'
import * as SiteStore from '@store/site/site'
import * as AuthStore from '@store/auth/auth'
import * as UserStore from '@store/user/user'
import * as UsersStore from '@store/users/users'

import useReducer from '@store/useReducer'
import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import theme from '@theme/Theme'
import { findFamilyParents, findLocalisationParents, displayLocationString } from '@screens/incident/utils'
import { findManifestation } from '@screens/manifestations/manifestationsUtils'
import { callPhoneNumber } from '@utils/userUtils'

import api from '@api/api'
import logger from '@utils/logger'

import { AuthScope, CommentaireType, EtatWorkflowIncidentEnum } from '@api/enum'

const HORIZONTAL_PADDING = 24

interface Props {
  incident: IncidentResponse
  commentStatus: Status
  comments: CommentaireResponse[]
  onCommentSubmitted: (comment: string, images: PieceJointeCommentaireRequest[]) => Promise<void>
  onProviderRelaunch?: () => void
  onAffectTechnician: () => void
  submitStatus: Status
  headerButtons: HeaderButton[]
  navigateToMap: () => void
  edlId?: string
  copyIncidentButton?: HeaderButton

  // Champs éditables par Viparis avant validation
  isPrioritaire: boolean
  onPriorityChange: () => void
  prestataireId?: string
  setPrestataireId: (id: string) => void
}

const IncidentDetailComponent = ({
  incident,
  commentStatus,
  comments,
  onCommentSubmitted,
  submitStatus,
  onProviderRelaunch,
  headerButtons,
  onAffectTechnician,
  isPrioritaire,
  onPriorityChange,
  prestataireId,
  setPrestataireId,
  navigateToMap,
  edlId,
  copyIncidentButton,
}: Props) => {
  const i18n = useI18n()
  const site = useReducer(SiteStore.store, (s) => s.site!)
  const services = useReducer(SocieteStore.store, (s) => s.services)
  const places = useReducer(LocationStore.store, (s) => s.places)
  const activities = useReducer(FamilleStore.store, (s) => s.activities)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)
  const connectedUser = useReducer(UserStore.store, (s) => s.user)
  const users = useReducer(UsersStore.store, (s) => s.users)

  const manifestationsStore = useReducer(SiteStore.store, (s) => s.manifestations)
  const manifestation = findManifestation(manifestationsStore, incident.manifestationId)

  const [technician, setTechnician] = React.useState<UserBasicResponse | UserResponse>()
  const [contact, setContact] = React.useState<UserBasicResponse | UserResponse>()
  const [commentors, setCommentors] = React.useState<(UserBasicResponse | UserResponse)[]>([])

  const [history, setHistory] = React.useState<IncidentHistoryResponse[]>([])
  const [historyUsers, setHistoryUsers] = React.useState<UserBasicResponse[]>([])

  const navigation = useNavigation()
  const [, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const [files, setFiles] = React.useState<FileDetailResponse[]>([])
  const [showContact, setShowContact] = React.useState(false)
  const provider = services.find((s) => s.id === (prestataireId || incident.societePrestataireId))

  const space = places.find((p) => p.id === incident.localisationId)
  const locationParents = space ? findLocalisationParents(places, space) : undefined

  const location = displayLocationString(incident, locationParents || [], manifestation)

  React.useEffect(() => {
    Drawer.close()
  }, [isPhone])

  React.useEffect(() => {
    api.incident
      .history(incident.id)
      .then((list) => setHistory(list.historique))
      .catch(logger.error)
  }, [incident])

  React.useEffect(() => {
    if (incident.technicienAffecteId) {
      const found = users.find((u) => u.id === incident.technicienAffecteId)

      if (found) {
        setTechnician(found)
      } else {
        api.users.get(incident.technicienAffecteId).then(setTechnician).catch(logger.error)
      }
    }
  }, [incident.technicienAffecteId, users])

  React.useEffect(() => {
    if (incident.utilisateurDeclarantId) {
      const found = users.find((u) => u.id === incident.utilisateurDeclarantId)

      if (found) {
        setContact(found)
      } else {
        api.users.get(incident.utilisateurDeclarantId).then(setContact).catch(logger.error)
      }
    }
  }, [incident.utilisateurDeclarantId, users])

  React.useEffect(() => {
    const currentIds = commentors.map((c) => c.id)
    const newIds = comments.map((c) => c.utilisateurId).filter((id) => !currentIds.includes(id))

    const found = users.filter((u) => newIds.includes(u.id))
    const notFoundIds = newIds.filter((id) => !found.find((u) => u.id === id))

    Promise.all(notFoundIds.map((id) => api.users.get(id)))
      .then((res) => setCommentors([...commentors, ...found, ...res]))
      .catch(logger.error)
  }, [comments, users])

  React.useEffect(() => {
    const historyUserIds = Array.from(new Set(history.map((c) => c.utilisateurId || ''))).filter(Boolean)
    const notFoundIds = historyUserIds.filter((id) => !users.find((u) => u.id === id))

    Promise.all(notFoundIds.map(api.users.get)).then(setHistoryUsers).catch(logger.error)
  }, [history, users])

  React.useEffect(() => {
    const commentAttachedFiles = comments
      .filter((c) => c.pieceJointeIds.length !== 0)
      .map((c) => c.pieceJointeIds)
      .flat()

    const attachedFiles = [...commentAttachedFiles, ...incident.photoIds]

    Promise.all(attachedFiles.map((id) => api.files.get(id)))
      .then((fs) => setFiles(fs))
      .catch(logger.error)
  }, [comments])

  if (!connectedUser) {
    return null
  }

  const headerLinks = edlId
    ? [
        { label: i18n.t(`screens.site.sites.${site.codeLieu}`), onClick: () => navigation.push('/') },
        { label: i18n.t(`screens.edl.title`), onClick: navigation.goBack },
      ]
    : [
        { label: i18n.t(`screens.site.sites.${site.codeLieu}`), onClick: () => navigation.push('/') },
        { label: i18n.t('screens.incidents.title'), onClick: () => navigation.push('/incidents') },
      ]

  const onPrimary = connectedUser.typeUtilisateur === 'EXPOSANT'

  const renderContact = (contact?: UserResponse) => (
    <InfoContainer>
      <Subtitle>{i18n.t('screens.incidentDetail.creator')}</Subtitle>
      {!!contact ? (
        <ContactInfoContainer>
          <View>
            <Info>{`${contact.prenom} ${contact.nom}`}</Info>
            {!!contact.email && <Info>{contact.email}</Info>}
          </View>
          {bp === 'phone' && !!contact.numeroTelephonePortable && (
            <PhoneTo onPress={() => callPhoneNumber(contact.numeroTelephonePortable)}>
              <Icon name="phone" color={theme.colors.primary} />
            </PhoneTo>
          )}
        </ContactInfoContainer>
      ) : (
        <Info>{i18n.t('screens.incidentDetail.unknownAuthor')}</Info>
      )}

      {!!contact && !!contact.numeroTelephonePortable && <Info>{contact.numeroTelephonePortable}</Info>}
    </InfoContainer>
  )
  const renderDates = () => (
    <LinksRow>
      {history.length > 0 || incident.datePriseEnCharge ? (
        <HistoryLink
          onPrimary={isPhone || onPrimary}
          onPress={() => Modal.open({ Content: renderHistory, position: 'top' })}>
          {i18n.t('screens.incidentDetail.history.open')}
        </HistoryLink>
      ) : (
        <IncidentDate onPrimary={onPrimary}>
          {i18n.t('screens.incidentDetail.creationDate', { date: new Date(incident.dateDeCreation) })}
        </IncidentDate>
      )}
      {copyIncidentButton && (
        <HistoryLink onPress={copyIncidentButton.onClick} onPrimary={isPhone || onPrimary}>
          {copyIncidentButton.label}
        </HistoryLink>
      )}
    </LinksRow>
  )

  const renderBottom = () => (
    <HeaderBottom>
      {renderDates()}
      <StatusContainer>
        {renderPriorityTag()}
        <IncidentStatus incidentStatus={incident.status} size="large" />
      </StatusContainer>
    </HeaderBottom>
  )

  const renderComment = (comment: CommentaireResponse) => {
    const author = commentors.find((u) => u.id === comment.utilisateurId)
    const authorName = !author ? i18n.t('screens.incidentDetail.unknownAuthor') : `${author.prenom} ${author.nom}`

    const presta = services.find((s) => s.id === comment.prestataireId)
    const newPresta = services.find((s) => s.id === comment.nouveauPrestataireId)

    const commentaire =
      comment.commentaireType === CommentaireType.REFUS_MAUVAIS_PRESTATAIRE && !comment.commentaire
        ? i18n.t('screens.incidentDetail.comment.REFUS_MAUVAIS_PRESTATAIRE_empty')
        : i18n.t(`screens.incidentDetail.comment.${comment.commentaireType}`, {
            commentaire: comment.commentaire,
            presta: presta ? presta.nameSociete : '',
            newPresta: newPresta ? newPresta.nameSociete : '',
            user: authorName,
          })

    return (
      <CommentContainer key={comment.id}>
        <CommentContent>
          <Comment>{commentaire}</Comment>
          <ImageContainer>
            {comment.pieceJointeIds.length > 0 && comment.pieceJointeIds.map(renderAttached)}
          </ImageContainer>
        </CommentContent>
        <CommentInfo>
          {i18n.t('screens.incidentDetail.commentInfo', { date: new Date(comment.creationDate), author: authorName })}
        </CommentInfo>
      </CommentContainer>
    )
  }

  const renderAttached = (id: string) => {
    const file = files.find((f) => f.id === id)

    if (!file) {
      return null
    }

    if (file.fileType === 'IMAGE') {
      return (
        <TouchableOpacity key={id} onPress={() => Linking.openURL(file.url)} activeOpacity={0.8}>
          <AttachedImage source={{ uri: file.url }} />
        </TouchableOpacity>
      )
    }

    return (
      <Document key={id} onPress={() => Linking.openURL(file.url)} activeOpacity={0.8}>
        <Icon name="document" size={50} />
        <DocumentName>{file.filename}</DocumentName>
      </Document>
    )
  }

  // TODO : définir la règle
  const canChangeProvider =
    scopes.includes(AuthScope.DemandesPrestataireAffectation) &&
    (incident.status === 'EN_ATTENTE_DE_VALIDATION' ||
      incident.status === 'EN_ATTENTE_DE_PRISE_EN_CHARGE' ||
      incident.status === 'REFUS_PRESTATAIRE' ||
      incident.status === 'EN_ATTENTE_DE_DEVIS' ||
      incident.status === 'DEVIS_ENVOYE' ||
      incident.status === 'EN_PAUSE')

  const canChangePriority =
    scopes.includes(AuthScope.DemandesPrioritaireCreation) &&
    (incident.status === 'EN_ATTENTE_DE_VALIDATION' || incident.status === 'REFUS_PRESTATAIRE')

  const canRelaunchProvider =
    scopes.includes(AuthScope.DemandesWorkflowReouverture) &&
    (incident.status === 'EN_ATTENTE_DE_PRISE_EN_CHARGE' || incident.status === 'EN_ATTENTE_DE_DEVIS')

  const CommentsWrapper = ({ children }: { children: React.ReactNode }) => {
    if (isPhone) {
      return <CommentSectionMobile>{children}</CommentSectionMobile>
    }
    return (
      <CommentSection keyboardShouldPersistTaps="handled" scrollEventThrottle={16}>
        {children}
      </CommentSection>
    )
  }

  const renderComments = () => {
    if (commentStatus === 'loading') {
      return (
        <CenteredContainer>
          <Loader />
        </CenteredContainer>
      )
    }

    return (
      <>
        <CommentTitle>
          <IconContainer>
            <Icon name="comment" size={24} color={theme.colors.primary} />
          </IconContainer>
          <Title>{i18n.t('screens.incidentDetail.comments')}</Title>
        </CommentTitle>
        <CommentsWrapper>
          {incident.photoIds.length > 0 && (
            <CreationFilesContainer>
              <CreationFiles>{incident.photoIds.map(renderAttached)}</CreationFiles>
              <CommentInfo>{i18n.t('screens.incidentDetail.creationFile')}</CommentInfo>
            </CreationFilesContainer>
          )}

          {comments.length > 0 ? (
            comments.map(renderComment)
          ) : (
            <Info>{i18n.t('screens.incidentDetail.noComment')}</Info>
          )}
          {isPhone && (
            <Button
              status="active"
              label={i18n.t('screens.incidentDetail.addComment')}
              onClick={() =>
                Drawer.open({
                  render: () => (
                    <DrawerContainer>
                      <IncidentCommentInput onSubmit={onCommentSubmitted} submitStatus={submitStatus} />
                    </DrawerContainer>
                  ),
                  hideTarget: true,
                  backgroundColor: theme.colors.incident,
                })
              }
              color="light"
            />
          )}
        </CommentsWrapper>
      </>
    )
  }

  const renderHistory = () => {
    const hasStart = !!history.find((h) => !h.from)

    const ouverture: IncidentHistoryResponse | undefined = !hasStart
      ? {
          id: 'START',
          incidentId: incident.id,
          dateChangementDeStatus: incident.dateDeCreation,
          to: EtatWorkflowIncidentEnum.EN_ATTENTE_DE_VALIDATION,
          utilisateurId: incident.utilisateurDeclarantId,
        }
      : undefined

    const priseEnCharge: IncidentHistoryResponse | undefined = !!incident.datePriseEnCharge
      ? {
          id: 'PRISE_EN_CHARGE',
          incidentId: incident.id,
          dateChangementDeStatus: incident.datePriseEnCharge,
          to: EtatWorkflowIncidentEnum.EN_COURS_DE_TRAITEMENT,
          utilisateurId: incident.technicienAffecteId || '',
        }
      : undefined

    const items = ([ouverture, priseEnCharge, ...history].filter(Boolean) as IncidentHistoryResponse[]).sort((a, b) =>
      a.dateChangementDeStatus.localeCompare(b.dateChangementDeStatus)
    )

    return (
      <History>
        <Close activeOpacity={0.8} onPress={() => Modal.close()}>
          <Icon name="close" size={20} />
        </Close>

        <Row>
          <ItemInfo>
            <ItemTitleText>{i18n.t(`screens.incidentDetail.history.titles.date`)}</ItemTitleText>
          </ItemInfo>
          <ItemInfo>
            <ItemTitleText> {i18n.t(`screens.incidentDetail.history.titles.user`)}</ItemTitleText>
          </ItemInfo>
          <ItemInfo>
            <ItemTitleText> {i18n.t(`screens.incidentDetail.history.titles.status`)}</ItemTitleText>
          </ItemInfo>
        </Row>

        <ItemSeparator />

        <FlatList
          data={items}
          renderItem={renderHistoryItem}
          keyExtractor={(item) => item.id}
          ItemSeparatorComponent={() => <ItemSeparator />}
        />
      </History>
    )
  }

  const renderHistoryItem = ({ item }: { item: IncidentHistoryResponse }) => {
    const user = [...users, ...historyUsers].find((u) => u.id === item.utilisateurId)

    const utilisateurChangement =
      item.id === 'PRISE_EN_CHARGE' && technician
        ? `${technician.nom} ${technician.prenom}`
        : !item.utilisateurId
        ? i18n.t('screens.incidentDetail.autoChange')
        : !!user
        ? `${user.nom} ${user.prenom}`
        : i18n.t('screens.incidentDetail.unknownAuthor')

    const status = item.id === 'PRISE_EN_CHARGE' ? item.id : item.from ? item.to : 'CREATED'

    return (
      <Row>
        <ItemInfo>
          <ItemInfoText>
            {i18n.t(`screens.incidentDetail.history.date`, { date: new Date(item.dateChangementDeStatus) })}
          </ItemInfoText>
        </ItemInfo>
        <ItemInfo>
          <ItemInfoText>{utilisateurChangement}</ItemInfoText>
        </ItemInfo>
        <ItemInfo>
          <ItemInfoText>{i18n.t(`incidentStatus.${status}`)}</ItemInfoText>
        </ItemInfo>
      </Row>
    )
  }

  const renderPriorityTag = () => {
    if (incident.isPrioritaire && incident.status === 'EN_ATTENTE_DE_PRISE_EN_CHARGE') {
      return (
        <HighPriorityContainer>
          <Icon name="problem" size={24} color={theme.colors.alert} />
          <HighPriorityLabel>{i18n.t(`screens.incidentDetail.highPriority`)}</HighPriorityLabel>
        </HighPriorityContainer>
      )
    }
    return null
  }

  const displayActivities = () => {
    const activity = activities.find((a) => a.id === incident.famillePrestationId)

    if (!activity) {
      return ''
    }

    const parents = findFamilyParents(activities, activity)
    return parents.map((p) => p.libelle).join(' / ')
  }

  return (
    <Container>
      {!isPhone && (
        <Header
          title={i18n.t('screens.incidentDetail.incidentNumber', {
            incidentNumber: incident.numero,
          })}
          buttons={headerButtons}
          links={headerLinks}
          renderBottom={renderBottom}
          color={connectedUser.typeUtilisateur === 'EXPOSANT' ? 'primary' : undefined}
        />
      )}

      <MainContainer>
        <LeftContainer keyboardShouldPersistTaps="handled" scrollEventThrottle={16}>
          {isPhone && (
            <MobileHeader>
              {renderDates()}

              {renderPriorityTag()}
              <IncidentStatus incidentStatus={incident.status} size="large" />
              <IncidentManifestation
                technician={technician as UserResponse}
                contact={contact as UserResponse}
                manifestation={manifestation}
              />
            </MobileHeader>
          )}

          <AllInfos>
            {bp !== 'phone' && (
              <TouchableOpacity activeOpacity={0.9} onPress={() => setShowContact(!showContact)}>
                <InfoTitle>
                  <IconContainer>
                    <Icon name="phone" size={24} color={theme.colors.primary} />
                  </IconContainer>
                  <Title>{i18n.t('screens.incidentDetail.contact')}</Title>
                  <Icon name={showContact ? 'arrow-up' : 'arrow-down'} size={24} color={theme.colors.primary} />
                </InfoTitle>
              </TouchableOpacity>
            )}
            {/*//TODO: différencier owner et creator*/}
            {showContact && <ContactContainer>{renderContact(contact as UserResponse)}</ContactContainer>}

            <Separator />

            <InfoTitle>
              <IconContainer>
                <Icon name="location" color={theme.colors.primary} size={24} />
              </IconContainer>
              <Title>{i18n.t('screens.incidentDetail.location')}</Title>
            </InfoTitle>

            <InfoContainer row>
              <Info>{location || i18n.t('screens.incidentList.card.unknownLocation')}</Info>
            </InfoContainer>

            <Separator />

            <InfoTitle>
              <IconContainer>
                <Icon name="hammer" size={24} color={theme.colors.primary} />
              </IconContainer>
              <Title>{i18n.t('screens.incidentDetail.caracteristique')}</Title>
            </InfoTitle>
            <InfoContainer>
              <Info>
                {i18n.t(`screens.incidentDetail.${incident.isInfrastructure ? 'infrastructure' : 'prestation'}`)}
              </Info>
              <Info>{displayActivities()}</Info>
              <Info>{i18n.t('screens.incidentDetail.quantite', { quantite: incident.quantite })}</Info>
            </InfoContainer>

            <Separator />

            <InfoTitle>
              <IconContainer>
                <Icon name="prestataire" size={24} color={theme.colors.primary} />
              </IconContainer>
              <Title>{i18n.t('screens.incidentDetail.provider')}</Title>
              {canChangeProvider && (
                <ModalSearch
                  title={i18n.t('screens.incidentDetail.changeProvider')}
                  subtitle={''}
                  data={services.filter((s) => s.enabled).map((p) => ({ label: p.nameSociete, value: p.id }))}
                  onItemSelected={setPrestataireId}
                  value={prestataireId}>
                  <ButtonLabel>{i18n.t('screens.incidentDetail.changeProvider')}</ButtonLabel>
                </ModalSearch>
              )}
            </InfoTitle>
            {!isPhone && !!technician && (
              <InfoContainer>
                <TechnicianContainer>
                  <TechnicianName>
                    {i18n.t('screens.incidentDetail.technician', {
                      firstName: technician.prenom,
                      lastName: technician.nom,
                    })}
                  </TechnicianName>
                </TechnicianContainer>
              </InfoContainer>
            )}

            {provider && (
              <InfoContainer>
                <Info>{provider.nameSociete}</Info>
              </InfoContainer>
            )}

            {!provider && (
              <InfoContainer>
                <Info>{i18n.t('screens.incidentDetail.noProvider')}</Info>
              </InfoContainer>
            )}

            {!technician && provider && (
              <InfoContainer>
                <Info>{i18n.t('screens.incidentDetail.noTechnician')}</Info>
              </InfoContainer>
            )}

            {canChangePriority && (
              <SwitchContainer>
                <Switch
                  value={isPrioritaire}
                  onChange={onPriorityChange}
                  trackColor={theme.colors.backgroundTertiary}
                />
                <SwitchLabel>{i18n.t('screens.incidentDetail.priority')}</SwitchLabel>
              </SwitchContainer>
            )}

            {canRelaunchProvider && onProviderRelaunch && (
              <ButtonContainer>
                <Button
                  status="active"
                  label={i18n.t('screens.incidentDetail.relaunchProvider')}
                  onClick={onProviderRelaunch}
                />
              </ButtonContainer>
            )}
            {isPhone && renderComments()}
          </AllInfos>
        </LeftContainer>

        {!isPhone && (
          <RightContainer>
            {renderComments()}
            <CommentInput>
              <IncidentCommentInput onSubmit={onCommentSubmitted} submitStatus={submitStatus} />
            </CommentInput>
          </RightContainer>
        )}
      </MainContainer>
    </Container>
  )
}

export default IncidentDetailComponent

const Container = styled(View)`
  flex: 1;
`
const MainContainer = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  flex: 1;
  z-index: -1;
`
const LeftContainer = styled(ScrollView)`
  flex: 1;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `0px 0 24px 0px` : `${12 + LARGE_SIZE / 2}px 0 24px`)};
`
const RightContainer = styled(View)`
  flex: 1;
  background-color: ${(props) => props.theme.colors.cardSecondary};
  padding-top: ${LARGE_SIZE / 2}px;
`
const CenteredContainer = styled(View)`
  align-items: center;
  justify-content: center;
  flex: 1;
  flex-direction: column;
`
const Title = styled(Text)`
  ${(props) => props.theme.fonts.Subheadline};
  flex: 1;
  font-size: 18px;
`
const InfoTitle = styled(View)`
  padding: 8px ${HORIZONTAL_PADDING}px;
  flex-direction: row;
  align-items: center;
`
const CommentTitle = styled(View)`
  padding: ${HORIZONTAL_PADDING}px ${HORIZONTAL_PADDING}px 8px;
  flex-direction: row;
  align-items: center;
`
const CommentSectionMobile = styled(View)`
  padding: 8px ${HORIZONTAL_PADDING}px;
`
const CommentSection = styled(ScrollView)`
  flex: 1;
  padding: 8px ${HORIZONTAL_PADDING}px;
`
const CommentInput = styled(View)`
  padding: ${HORIZONTAL_PADDING}px 0 ${HORIZONTAL_PADDING}px ${HORIZONTAL_PADDING}px;
  flex-direction: row;
`
const IconContainer = styled(View)`
  margin-right: 8px;
`
const Separator = styled(View)`
  height: ${(props) => (props.theme.breakpoint === 'phone' ? 0 : 1)}px;
  background-color: ${(props) => props.theme.colors.disable};
  margin: ${(props) => (props.theme.breakpoint === 'phone' ? 8 : 16)}px ${HORIZONTAL_PADDING}px;
`
const IncidentDate = styled(Text)<{ onPrimary?: boolean }>`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Footnote : props.theme.fonts.Body)};

  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? `
      text-align: center;
      color: ${props.theme.colors.background};
      margin-bottom: 16px;`
      : `
      color: ${props.theme.colors.secondaryText};
      padding-top: 16px;`}

  ${(props) => props.onPrimary && `color: ${props.theme.colors.background};`}
`
const HeaderBottom = styled(View)`
  margin-bottom: -${16 + LARGE_SIZE / 2}px;
`
const StatusContainer = styled(View)`
  margin-top: 22px;
`

const InfoContainer = styled(View)<{ row?: boolean }>`
  padding: 8px ${HORIZONTAL_PADDING}px;
  ${(props) => props.row && 'flex-direction: row; justify-content: space-between; align-items: center;'};
`
const Info = styled(Text)`
  margin-bottom: 8px;
  ${(props) => props.theme.fonts.Footnote};
  font-size: 16px;
  color: ${(props) => props.theme.colors.secondaryText};
`
const ContactContainer = styled(View)`
  margin-bottom: 8px;
`
const Subtitle = styled(Text)`
  ${(props) => props.theme.fonts.Callout};
  margin-bottom: 8px;
`
const ButtonContainer = styled(View)`
  max-width: 250px;
  margin-top: 24px;
  align-self: center;
`
const SwitchContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  padding: 0 ${HORIZONTAL_PADDING}px;
  margin-top: 32px;
`
const SwitchLabel = styled(Text)`
  margin-left: 16px;
  ${(props) => props.theme.fonts.Body};
`
const CommentContainer = styled(View)`
  margin-bottom: 16px;
`
const CommentContent = styled(View)`
  background-color: ${(props) => props.theme.colors.background};
  padding: 14px 16px;
  border-radius: 6px;
  margin-bottom: 8px;
  border: 1px solid ${(props) => props.theme.colors.secondaryActive};
`
const Comment = styled(Text)`
  ${(props) => props.theme.fonts.Body};
`
const ImageContainer = styled(View)`
  margin-top: 8px;
  align-items: flex-start;
`
const CommentInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => props.theme.colors.tertiary};
`
const MobileHeader = styled(View)`
  background-color: ${(props) => props.theme.colors.primary};
  padding: 16px 16px 64px;
  margin-bottom: -56px;
`

const AllInfos = styled(View)`
  ${(props) =>
    props.theme.breakpoint === 'phone' &&
    `
    border-radius: 8px;
    margin: 16px;
    background-color: ${props.theme.colors.background};
    padding: 16px 0px;`}
`
const ButtonLabel = styled(Text)`
  ${(props) => props.theme.fonts.Callout};
  color: ${(props) => props.theme.colors.primary};
  text-align: center;
`
const DrawerContainer = styled(View)`
  flex: 1;
`
const TechnicianContainer = styled(View)`
  flex-direction: row;
`
const TechnicianName = styled(Text)`
  padding: 6px 8px;
  border-radius: 4px;
  background-color: ${(props) => props.theme.colors.secondaryActive};
  ${(props) => props.theme.fonts.Footnote};
  font-size: 16px;
`
const HighPriorityContainer = styled(View)`
  height: ${LARGE_SIZE}px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.priority};
  padding: 4px 16px 12px;
  flex-direction: row;
  align-items: center;
  margin-bottom: -12px;
`
const HighPriorityLabel = styled(Text)`
  ${(props) => props.theme.fonts.Caption1};
  font-size: 16px;
  margin-left: 14px;
`
const AttachedImage = styled(Image)`
  height: 50px;
  width: 50px;
  background-color: ${(props) => props.theme.colors.disable};
`

const Document = styled(TouchableOpacity)`
  align-items: center;
  width: 60px;
`

const DocumentName = styled(Text)`
  ${(props) => props.theme.fonts.Caption1};
  margin-top: 5px;
`
const CreationFilesContainer = styled(View)`
  margin-bottom: 16px;
`
const CreationFiles = styled(View)`
  flex-direction: row;
  margin-bottom: 16px;
`
const ContactInfoContainer = styled(View)`
  flex-direction: row;
  justify-content: space-between;
`
const PhoneTo = styled(TouchableOpacity)`
  justify-content: center;
  align-items: center;
`

// history
const LinksRow = styled(View)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`

const HistoryLink = styled(IncidentDate)<{ onPrimary?: boolean }>`
  text-decoration: underline;
  align-self: flex-start;
  text-decoration-color: ${(props) =>
    props.onPrimary ? props.theme.colors.background : props.theme.colors.secondaryText};
  ${Platform.OS === 'web' && 'cursor: pointer;'}
`

const History = styled(View)`
  ${(props) => props.theme.breakpoint === 'big' && 'width: 600px;'};
  background-color: ${(props) => props.theme.colors.background};
  border-radius: 10px;
  padding: 15px ${(props) => (props.theme.breakpoint === 'phone' ? 5 : 15)}px;
  max-height: 500px;
  min-width: 340px;
`

const Row = styled(View)`
  flex-direction: row;
  align-items: center;
`

const ItemInfo = styled(View)`
  flex: 1;
  padding: 15px 5px;
`

const ItemInfoText = styled(Text)`
  ${(props) => props.theme.fonts.Option};
`

const ItemTitleText = styled(Text)`
  ${(props) => props.theme.fonts.Subheadline};
`

const ItemSeparator = styled(View)`
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
  height: 2px;
`

const Close = styled(TouchableOpacity)`
  align-self: flex-end;
  padding: 5px;
`
